import { HomepageProductCard } from "@modules/Homepage/HomepageProductCard";
import { useGetCollectionByHandleQuery } from "@modules/shopify/hooks";
import { useLocale } from "@modules/shared/hooks/useLocale";
import { useTranslation } from "react-i18next";
import type { Product } from "@shopify/hydrogen-react/storefront-api-types";
import { getCountryVariables } from "@/modules/shared/localization/const/countries";

type Props = {
  searchQuery: string;
};

export const SearchNotFoundRecommendation: React.FC<Props> = ({
  searchQuery,
}) => {
  const locale = useLocale();
  const { t } = useTranslation("Navbar");

  const { data, isLoading, error } = useGetCollectionByHandleQuery({
    handle: "novi-proizvodi-1",
    numberOfProducts: 5,
    ...getCountryVariables(locale),
  });

  const products = data?.collection?.products.nodes || [];
  const normalizedProducts = products.map((product) => ({
    ...product,
    compareAtPriceRange: product.compareAtPriceRange ?? {
      maxVariantPrice: product.priceRange.maxVariantPrice,
      minVariantPrice: product.priceRange.minVariantPrice,
    },
  }));

  if (error || !normalizedProducts.length || isLoading) return null;

  return (
    <div className="grid grid-cols-40-dynamic pb-10 pt-6 max-laptop:grid-cols-40-fixed max-mobile:grid-cols-15 max-mobile:px-[calc(100%/15)] max-mobile:py-6 [&>*]:col-span-padded overflow-auto">
      <div className="col-span-padded w-full max-mobile:col-span-full">
        <div className="mx-auto mb-8 max-w-2xl rounded-[28px] border border-dashed border-border-5 bg-background-7 px-6 py-8 text-center">
          <p className="font-secondary text-large font-medium -tracking-[0.02em] text-text-1 max-mobile:text-medium">
            {t("search.notFound", {
              term: searchQuery,
            })}
          </p>
          <p className="mt-2 text-small text-text-3">{t("search.tryThese")}</p>
        </div>
      </div>
      <div className="col-span-padded w-full max-mobile:col-span-full">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {normalizedProducts.map((product) => (
            <HomepageProductCard
              key={product.id}
              product={product as Product}
              basePriceVariants={product.basePriceVariants.nodes}
              productItemClassName="h-full w-full min-w-0 max-w-none gap-4 rounded-[24px] border border-border-5 bg-background-7 p-3 transition-all hover:shadow-lg"
              productItemTitleClassName="text-small leading-smallMedium"
              productPriceClassName="text-small leading-smallMedium"
              productImageClassName="h-full w-full object-cover"
              imageWidth={220}
              imageHeight={320}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
